/**
 * Preview-only annotations: marks drawn on a separate layer (e.g. an overlay
 * canvas) to explain the label, never part of it. They are not label ink, so
 * they are not in measureContent() and never affect clipping.
 *
 * The "no data" note follows NS's barcode placeholder look (dashed #aaa
 * outline, #888 sans-serif text, ns-web label-viewer barcode-renderer.js),
 * drawn around the real printer stub rather than instead of it.
 */

import type { BarcodeElement, LabelDoc } from './document-model';
import type { LabelCanvasContext } from './canvas-context';
import { encodeBarcode, encodedSize, loadedBarcodeEncoder } from './barcode-renderer';
import { printsEmptySymbol } from './barcode-types';
import { pxPerDot } from './unit-converter';

/** A barcode field with no data: where it is and what (if anything) the printer prints. */
export interface EmptyBarcode {
  index: number;
  /** The field's ^FO (label home applied), to match the preview endpoint's empty_fields. */
  origin: { x: number; y: number };
  /** Ink the printer still prints (the Code 128 stub), in dots; null if it prints nothing. */
  ink: { x: number; y: number; w: number; h: number } | null;
}

/** One annotation: a dashed box around a region of the label, with a short caption. */
export interface LabelAnnotation {
  /** Region in label dots the box must surround (it grows to fit the caption). */
  box: { x: number; y: number; w: number; h: number };
  text: string;
}

/** Warning text for a barcode with empty data (also used to recognise it in the warnings). */
export function emptyBarcodeWarning(el: BarcodeElement): string {
  return printsEmptySymbol(el.barcodeType)
    ? `Barcode at ${el.x},${el.y} has no data; the printer prints an empty barcode`
    : `Barcode at ${el.x},${el.y} has no data; nothing will print`;
}

/** Barcodes with empty data in a document, with the stub ink the printer still prints. */
export function findEmptyBarcodes(doc: LabelDoc, dpi: number): EmptyBarcode[] {
  const out: EmptyBarcode[] = [];
  const encoder = loadedBarcodeEncoder();
  doc.elements.forEach((el, index) => {
    if (el.type !== 'barcode' || el.data !== '') return;
    let ink: EmptyBarcode['ink'] = null;
    if (printsEmptySymbol(el.barcodeType) && encoder) {
      try {
        const size = encodedSize(encodeBarcode(encoder, el, dpi));
        ink = { x: el.x, y: el.y, ...size };
      } catch {
        ink = null;
      }
    }
    out.push({ index, origin: el.origin, ink });
  });
  return out;
}

const MARGIN_DOTS = 4;
const MIN_BOX_PX = { w: 60, h: 28 };
const FONT_PX = 10;
const PAD_PX = 6;

/**
 * Draw annotations onto their own context, sized like the label canvas
 * (same dpi / zoom). pixelRatio scales the caption so it stays a fixed size
 * on screen. Returns each box in canvas pixels.
 */
export function renderAnnotations(
  ctx: LabelCanvasContext,
  annotations: LabelAnnotation[],
  options: { dpi: number; zoom?: number; pixelRatio?: number },
): { x: number; y: number; w: number; h: number }[] {
  const scale = pxPerDot(options.dpi, options.zoom ?? 1);
  const ratio = options.pixelRatio ?? 1;
  const font = FONT_PX * ratio;
  const boxes: { x: number; y: number; w: number; h: number }[] = [];

  for (const a of annotations) {
    ctx.save();
    ctx.font = `normal ${font}px sans-serif`;
    const textW = ctx.measureText(a.text).width;
    const inkX = (a.box.x - MARGIN_DOTS) * scale;
    const inkY = (a.box.y - MARGIN_DOTS) * scale;
    const inkW = (a.box.w + 2 * MARGIN_DOTS) * scale;
    const inkH = (a.box.h + 2 * MARGIN_DOTS) * scale;
    const w = Math.max(inkW, textW + 2 * PAD_PX * ratio, MIN_BOX_PX.w * ratio);
    const h = Math.max(inkH + font + PAD_PX * ratio, MIN_BOX_PX.h * ratio);
    const box = { x: inkX, y: inkY, w, h };

    ctx.strokeStyle = '#aaaaaa';
    ctx.lineWidth = ratio;
    ctx.setLineDash([4 * ratio, 4 * ratio]);
    ctx.beginPath();
    roundedRect(ctx, box.x + 0.5, box.y + 0.5, box.w, box.h, 4 * ratio);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = '#888888';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText(a.text, box.x + box.w / 2, box.y + box.h - (PAD_PX / 2) * ratio);
    ctx.restore();
    boxes.push(box);
  }
  return boxes;
}

function roundedRect(ctx: LabelCanvasContext, x: number, y: number, w: number, h: number, r: number): void {
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
